/**
 * Planned-hole schedule helpers: primary dates from estimated_complete + days_estimate,
 * with optional fallback to legacy start_date for ordering/display.
 */

/** @param {Record<string, unknown>} row */
export function plannedDrillDays(row) {
  const n = Number(row.days_estimate);
  if (Number.isFinite(n) && n > 0) return Math.max(1, Math.floor(n));
  return 1;
}

/** @returns {{ y: number, mo: number, d: number } | null} */
export function parseDateOnlyParts(s) {
  if (s == null || s === '') return null;
  const m = String(s).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return { y: +m[1], mo: +m[2], d: +m[3] };
}

/**
 * Anchor for ordering / time-fade: first calendar day of the drilling window (UTC date math).
 * @param {Record<string, unknown>} row
 */
export function plannedAnchorMs(row) {
  const parts = parseDateOnlyParts(row.estimated_complete);
  if (parts) {
    const d = plannedDrillDays(row);
    const endMs = Date.UTC(parts.y, parts.mo - 1, parts.d, 12, 0, 0);
    return endMs - (d - 1) * 86400000;
  }
  if (row.start_date == null || row.start_date === '') return Number.POSITIVE_INFINITY;
  const t = Date.parse(String(row.start_date));
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
}

/**
 * Human-readable schedule line for tooltips / detail panel.
 * @param {Record<string, unknown>} row
 */
/** Estimated completion date as YYYY-MM-DD, or null. */
export function estimatedCompleteIso(row) {
  const parts = parseDateOnlyParts(row.estimated_complete);
  if (!parts) return null;
  const pad2 = (n) => String(n).padStart(2, '0');
  return `${parts.y}-${pad2(parts.mo)}-${pad2(parts.d)}`;
}

export function formatPlannedScheduleLabel(row) {
  const parts = parseDateOnlyParts(row.estimated_complete);
  if (!parts) {
    if (row.start_date || row.end_date) {
      return `${row.start_date ?? ''}${row.end_date ? ` → ${row.end_date}` : ''}`;
    }
    return '—';
  }
  const n = plannedDrillDays(row);
  const endMs = Date.UTC(parts.y, parts.mo - 1, parts.d, 12, 0, 0);
  const startMs = endMs - (n - 1) * 86400000;
  const fmt = (ms) => new Date(ms).toISOString().slice(0, 10);
  if (n <= 1) return `${fmt(endMs)} · est. complete (${n}d)`;
  return `${fmt(startMs)} → ${fmt(endMs)} · ${n}d est.`;
}
